import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Phone, MapPin, Building2, Calendar, PhoneCall } from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { MaskReveal, Reveal } from "../components/Reveal";
import { useContactDialogs } from "../components/ContactDialogs";
import { SERVICES, OFFICES, mapsUrl, PHONE, PHONE_DISPLAY, OFFICE_FRIENDLY_IMG } from "../data/constants";

const HERO_IMG = "https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1400&q=80";
const OFFICE_IMG = OFFICE_FRIENDLY_IMG;

const MANIFESTO = [
  { n: "01", title: "Felmérés", body: "Meghallgatjuk céljait és feltérképezzük pénzügyi helyzetét — díjmentesen, kötelezettség nélkül." },
  { n: "02", title: "Összehasonlítás", body: "A piac ajánlatait objektíven összevetjük, hogy az Ön élethelyzetéhez illő megoldást találjuk." },
  { n: "03", title: "Ügyintézés", body: "A szerződéstől a folyósításig végigkísérjük, az adminisztrációt levesszük a válláról." },
];

function Hero() {
  const { openBooking, openCallback } = useContactDialogs();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section ref={ref} data-testid="hero" className="relative min-h-screen flex flex-col justify-end overflow-hidden pt-40 pb-16">
      <div className="noise-overlay z-10" />
      <div className="mx-auto max-w-[1400px] w-full px-6 lg:px-10 relative z-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
              className="overline text-muted-foreground mb-8 flex items-center gap-3">
              Szabó Ádám — Független pénzügyi tanácsadás
            </motion.div>

            <h1 className="font-serif-display font-medium tracking-tight leading-[0.95] text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem]">
              <MaskReveal lines={["Pénzügyi", "megoldások,"]} delay={0.3} />
              <MaskReveal lines={["emberi léptékkel."]} delay={0.55} lineClassName="italic text-muted-foreground" />
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.8 }}
              className="mt-8 max-w-md text-muted-foreground leading-relaxed">
              Hitel, munkáshitel, biztosítás, CSOK, Otthonstart, Babaváró és
              személyi kölcsön — egy helyen, átlátható tanácsadással, három megyei irodában.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05, duration: 0.8 }}
              className="mt-10 flex flex-col sm:flex-row gap-4">
              <Button data-testid="hero-booking-btn" onClick={openBooking}
                className="rounded-none bg-foreground text-background hover:bg-[#2A2A2A] h-14 px-8 text-base group">
                <Calendar className="mr-2 h-5 w-5" />
                Időpontfoglalás
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button data-testid="hero-callback-btn" onClick={openCallback} variant="outline"
                className="rounded-none border-foreground text-foreground hover:bg-secondary h-14 px-8 text-base group">
                <PhoneCall className="mr-2 h-5 w-5" />
                Telefonos visszahívás
              </Button>
            </motion.div>

            <motion.a
              href={`tel:${PHONE}`} data-testid="hero-call-link"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}
              className="mt-8 inline-flex items-center gap-2 text-sm text-muted-foreground link-underline">
              <Phone className="h-4 w-4" /> Azonnali hívás: {PHONE_DISPLAY}
            </motion.a>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 1 }}
              className="relative h-[340px] lg:h-[480px] overflow-hidden">
              <motion.img
                src={HERO_IMG} alt="Modern épület homlokzat" style={{ y, scale }}
                className="absolute inset-0 h-full w-full object-cover" />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Services() {
  const { openBooking } = useContactDialogs();
  const [selected, setSelected] = useState(null);

  return (
    <section id="szolgaltatasok" data-testid="services" className="border-t border-border">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-32">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
          <div>
            <span className="overline text-muted-foreground">Amiben segítünk</span>
            <Reveal>
              <h2 className="font-serif-display text-4xl lg:text-5xl tracking-tight mt-4 max-w-xl">
                Hét terület, egyetlen megbízható partner.
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <p className="text-muted-foreground max-w-sm leading-relaxed">
              Minden szolgáltatásunk mögött személyes tanácsadás áll — nem termékeket
              értékesítünk, hanem az Ön érdekét képviseljük. Kattintson a részletes
              feltételekért.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-l border-t border-border">
          {SERVICES.map((s, i) => (
            <Reveal key={s.id} delay={(i % 3) * 0.08}>
              <button
                onClick={() => setSelected(s)}
                data-testid={`service-card-${s.id}`}
                className="group relative text-left w-full h-full border-r border-b border-border p-10 hover:bg-secondary/50 transition-colors">
                <div className="flex items-start justify-between">
                  <span className="font-mono-label text-xs text-muted-foreground">0{i + 1}</span>
                  <ArrowUpRight className="h-5 w-5 text-muted-foreground opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-[opacity,transform]" />
                </div>
                <h3 className="font-serif-display text-2xl tracking-tight mt-8">{s.name}</h3>
                <span className="overline text-muted-foreground block mt-2">{s.tag}</span>
                <p className="text-sm text-muted-foreground leading-relaxed mt-5">{s.desc}</p>
                <span className="inline-flex items-center gap-1.5 mt-6 text-xs font-medium">
                  Feltételek megtekintése
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent data-testid="service-detail-dialog" data-lenis-prevent
          className="rounded-none border border-foreground bg-background p-0 sm:max-w-xl max-h-[92vh] overflow-y-auto gap-0">
          {selected && (
            <>
              <DialogHeader className="text-left space-y-2 p-8 pb-6 border-b border-border">
                <span className="overline text-muted-foreground">{selected.tag}</span>
                <DialogTitle className="font-serif-display text-3xl font-medium tracking-tight">
                  {selected.name}
                </DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  {selected.desc}
                </DialogDescription>
              </DialogHeader>
              <div className="p-8">
                <span className="overline text-muted-foreground">Főbb feltételek</span>
                <ul className="mt-5 space-y-4">
                  {selected.conditions.map((c, idx) => (
                    <li key={idx} className="grid grid-cols-[auto_1fr] gap-4">
                      <span className="font-mono-label text-xs text-muted-foreground mt-0.5">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <p className="text-sm text-muted-foreground leading-relaxed">{c}</p>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-muted-foreground/70 mt-6 leading-relaxed">
                  A feltételek tájékoztató jellegűek, a végleges elbírálás a pénzügyi intézmény hatásköre.
                </p>
                <Button data-testid="service-detail-booking-btn"
                  onClick={() => { setSelected(null); openBooking(); }}
                  className="w-full mt-8 rounded-none bg-foreground text-background hover:bg-[#2A2A2A] h-12 group">
                  Időpontfoglalás ehhez a szolgáltatáshoz
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function Manifesto() {
  return (
    <section data-testid="manifesto" className="bg-foreground text-background">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-32">
        <span className="overline text-background/50">A folyamat</span>
        <Reveal>
          <h2 className="font-serif-display text-4xl lg:text-5xl tracking-tight mt-4 max-w-2xl">
            Három lépés a nyugodt pénzügyi döntésekhez.
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-16 mt-20">
          {MANIFESTO.map((m, i) => (
            <Reveal key={m.n} delay={i * 0.12}>
              <div className="border-t border-background/20 pt-8">
                <span className="font-serif-display text-6xl text-background/30">{m.n}</span>
                <h3 className="font-serif-display text-2xl mt-6">{m.title}</h3>
                <p className="text-background/60 leading-relaxed mt-4 text-sm">{m.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function EditorialMarquee() {
  const words = ["Hitel", "Biztosítás", "CSOK", "Otthonstart", "Babaváró", "Személyi kölcsön", "Munkáshitel"];
  const groups = [0, 1]; // duplicated for seamless loop
  return (
    <div data-testid="marquee" className="marquee-viewport border-y border-border py-8 overflow-hidden bg-background">
      <div className="marquee-track">
        {groups.map((g) => (
          <div key={g} className="flex items-center shrink-0" aria-hidden={g === 1}>
            {words.map((w, i) => (
              <span key={i} className="font-serif-display text-4xl lg:text-6xl tracking-tight mx-10 flex items-center gap-10 whitespace-nowrap">
                {w}
                <span className="text-muted-foreground/40 text-3xl">—</span>
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function Offices() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="irodak" ref={ref} data-testid="offices" className="border-t border-border">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="overline text-muted-foreground">Irodáink</span>
            <Reveal>
              <h2 className="font-serif-display text-4xl lg:text-5xl tracking-tight mt-4">
                Keressen fel minket személyesen.
              </h2>
            </Reveal>
            <p className="text-muted-foreground leading-relaxed mt-6 max-w-sm">
              Ezek az irodák tárgyalás céljából állnak rendelkezésre — nem olyan
              irodák, ahol állandóan elérhető valaki és azonnali információval tud
              szolgálni. Kérjük, előzetes időpontfoglalással keressen fel minket.
              Kattintson bármelyik címre a Google Térképen való megnyitáshoz.
            </p>
            <div className="relative h-64 mt-10 overflow-hidden hidden lg:block">
              <motion.img src={OFFICE_IMG} style={{ y }} alt="Tárgyaló"
                className="absolute inset-0 h-[120%] w-full object-cover" />
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="border-t border-border">
              {OFFICES.map((o, i) => (
                <Reveal key={o.id} delay={i * 0.08}>
                  <a href={mapsUrl(o.query)} target="_blank" rel="noopener noreferrer"
                    data-testid={`office-card-${o.id}`}
                    className="group flex items-center justify-between gap-6 border-b border-border py-10 hover:px-4 transition-[padding] duration-300">
                    <div className="flex items-baseline gap-6">
                      <span className="font-mono-label text-xs text-muted-foreground">0{i + 1}</span>
                      <div>
                        <h3 className="font-serif-display text-2xl lg:text-3xl tracking-tight">{o.city}</h3>
                        <p className="text-muted-foreground text-sm mt-2 flex items-center gap-2">
                          <Building2 className="h-3.5 w-3.5" /> {o.address}
                        </p>
                      </div>
                    </div>
                    <MapPin className="h-6 w-6 text-muted-foreground shrink-0 group-hover:text-foreground group-hover:scale-110 transition-[transform,color] duration-300" />
                  </a>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  const { openBooking, openCallback } = useContactDialogs();
  return (
    <section data-testid="final-cta" className="border-t border-border">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-32 text-center">
        <Reveal>
          <span className="overline text-muted-foreground">Kezdjük el</span>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-6 max-w-3xl mx-auto leading-[1.05]">
            Beszéljük meg a lehetőségeit — <span className="italic text-muted-foreground">díjmentesen.</span>
          </h2>
          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <Button data-testid="cta-booking-btn" onClick={openBooking}
              className="rounded-none bg-foreground text-background hover:bg-[#2A2A2A] h-14 px-8 text-base group">
              <Calendar className="mr-2 h-5 w-5" /> Időpontfoglalás
              <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Button>
            <Button data-testid="cta-callback-btn" onClick={openCallback} variant="outline"
              className="rounded-none border-foreground text-foreground hover:bg-secondary h-14 px-8 text-base">
              <PhoneCall className="mr-2 h-5 w-5" /> Telefonos visszahívás
            </Button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <Services />
      <EditorialMarquee />
      <Manifesto />
      <Offices />
      <FinalCTA />
    </div>
  );
}
